import { useTranslation } from "react-i18next";
import GenericCrudTable from "@/components/tables/genericCrudTable";
import RestrictedPage from "@/consumer/restrictedPage";
import styles from "@/routes/admin/auditLog.module.css";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  OutlinedInput,
} from "@mui/material";
import { useEffect, useState } from "react";
import { api } from "@/helper/api";
import { validate, version } from "uuid";
import ClearIcon from "@mui/icons-material/Clear";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import { replaceDate } from "@/helper/dateHandling";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import { auditLogEntityAdapter } from "@/routes/admin/auditLogEntityAdapter";

const INITIAL_SEARCH_FOR_APPOINTMENT_ID = true;

export default function AuditLog() {
  const { t } = useTranslation();
  usePageHeading(t("audit_log"));

  const [searchForAppointmentId, setSearchForAppointmentId] =
    useState<boolean>(INITIAL_SEARCH_FOR_APPOINTMENT_ID);
  const [inputValue, setInputValue] = useState<string>("");
  const [auditLogData, setAuditLogData] = useState<any[]>([]);
  const [tableKeyCounter, setTableKeyCounter] = useState(0);
  const { handleError, errorModal } = useErrorModal();
  const hasAppointmentIdStateToReset =
    searchForAppointmentId !== INITIAL_SEARCH_FOR_APPOINTMENT_ID ||
    inputValue !== "" ||
    auditLogData.length > 0;

  const tableKey = `auditlog-table-${tableKeyCounter}`;

  const isUUID = (value: string): boolean =>
    validate(value) && version(value) === 4;

  useEffect(() => {
    if (inputValue !== "" && isUUID(inputValue)) {
      fetchAndAppendAuditlogData();
    }
  }, [inputValue]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const cleanedValue = event.target.value.trim().replace(/\s+/g, "");
    setInputValue(cleanedValue);
  };

  const resetAppointmentIdFilter = () => {
    if (!hasAppointmentIdStateToReset) return;

    setSearchForAppointmentId(INITIAL_SEARCH_FOR_APPOINTMENT_ID);
    setAuditLogData([]);
    setInputValue("");
    setTableKeyCounter((prev) => prev + 1);
  };

  const handleSearchForAppointmentIdCheck = (event: any) => {
    const newValue = event.target.checked;
    if (newValue === false) {
      setSearchForAppointmentId(false);
      setAuditLogData([]);
      setInputValue("");
      setTableKeyCounter((prev) => prev + 1);
      return;
    }
    setSearchForAppointmentId(true);
    setTableKeyCounter((prev) => prev + 1);
  };
  const handleClearInput = () => {
    setInputValue("");
    setAuditLogData([]);
  };

  const customTopToolbarAction = () => {
    return (
      <>
        <FormControlLabel
          label={t("search_for_appointmentid")}
          control={
            <Checkbox
              checked={searchForAppointmentId}
              onChange={(event) => handleSearchForAppointmentIdCheck(event)}
            />
          }
        />
        {searchForAppointmentId && (
          <FormControl
            sx={{ m: 0, width: "30vw" }}
            variant="outlined"
            size="small"
          >
            <InputLabel htmlFor="appointmentId">
              {t("appointment_id")}
            </InputLabel>
            <OutlinedInput
              id="appointmentId"
              onChange={(event: any) => handleInputChange(event)}
              value={inputValue}
              endAdornment={
                <InputAdornment position="end">
                  <GenericButton
                    onClick={handleClearInput}
                    variant="icon"
                    color="iconBase"
                  >
                    {inputValue !== "" && <ClearIcon />}
                  </GenericButton>
                </InputAdornment>
              }
              label={t("appointment_id")}
            />
          </FormControl>
        )}
      </>
    );
  };

  const mergeTableRows = (existingRows: any[], newData: any[]) => {
    const rowMap: Record<string, any> = {};
    existingRows.forEach((row) => {
      rowMap[row.id] = row;
    });
    newData.forEach((item) => {
      rowMap[item.id] = { ...item };
    });
    return Object.values(rowMap);
  };
  const fetchAndAppendAuditlogData = async () => {
    const pageSize = 50;

    const fetchData = async (limit: number, offset: number): Promise<any> => {
      const response = await new Promise((resolve, reject) => {
        api.genericApiRequest({
          entity: "auditlog",
          method: "get",
          additionalRouteParts: { appointment_trace: "/" },
          parametersToRender: {
            appointment_id: inputValue,
            depth: "0",
            limit: limit,
            offset: offset,
          },
          successHandler: (res: any) => resolve(res),
          failHandler: (err: any) => reject(err),
        });
      });
      return response;
    };

    try {
      const initialCallResponse = await fetchData(pageSize, 0);
      const firstResults = initialCallResponse.data.results;
      const totalCount = initialCallResponse.data.count;
      setAuditLogData((prev) => mergeTableRows(prev, firstResults));

      const remainingRows = totalCount - pageSize;
      if (remainingRows > 0) {
        const secondCallResponse = await fetchData(remainingRows, pageSize);
        const secondResults = secondCallResponse.data.results;
        setAuditLogData((prev) => mergeTableRows(prev, secondResults));
      }
    } catch (err) {
      handleError(err);
    }
  };

  return (
    <RestrictedPage permissionRequired="frontend_view_auditlog">
      <GenericCrudTable
        key={tableKey}
        allowColumnFiltering={true}
        showColumnFilterbyDefault={true}
        {...(auditLogData.length !== 0 && { customTableData: auditLogData })}
        allowPaginationWithCustomTableData={searchForAppointmentId}
        customTopToolBarAction={() => customTopToolbarAction()}
        disableGlobalFilter={searchForAppointmentId}
        entity={"auditlog"}
        entityAdapter={auditLogEntityAdapter}
        disallowDelete={true}
        disableAddItem={true}
        showRowActions={false}
        hasAdditionalFiltersToReset={hasAppointmentIdStateToReset}
        onResetAllFilters={resetAppointmentIdFilter}
        columns={[
          { header: t("id"), accessorKey: "id", id: "id" },
          {
            header: t("object_pk"),
            accessorKey: "object_pk",
            id: "object_pk",
          },
          {
            header: t("object_repr"),
            accessorKey: "object_repr",
            id: "object_repr",
          },
          {
            header: t("changes"),
            accessorKey: "changes",
            customColumnFilterId: "changes",
            customFilterParameter: "__icontains",
            id: "changes",
            size: 600,
            Cell: ({ cell }: any) => {
              const source = cell.getValue();
              const items = [];
              const indent = (level: number) => "  ".repeat(level);

              // Pretty-print stringified objects if possible, otherwise return as string
              function prettyPrintIfObject(val: any, level = 0): string {
                if (
                  typeof val === "string" &&
                  val.trim().startsWith("{") &&
                  val.trim().endsWith("}")
                ) {
                  try {
                    const parsed = JSON.parse(val);
                    // Only pretty-print if object has keys
                    if (
                      parsed &&
                      typeof parsed === "object" &&
                      Object.keys(parsed).length > 0
                    ) {
                      return (
                        "{\n" +
                        Object.entries(parsed)
                          .map(
                            ([k, v]) =>
                              indent(level + 1) +
                              `${k}: ${prettyPrintIfObject(v, level + 1)}`
                          )
                          .join("\n") +
                        "\n" +
                        indent(level) +
                        "}"
                      );
                    } else {
                      return "{}";
                    }
                  } catch {
                    // Not valid JSON, fall through
                  }
                }
                return String(val);
              }

              // Format value for display in the audit log changes column
              function formatValue(value: any, level = 0): string {
                if (Array.isArray(value)) {
                  // Handle [old, new] changes, pretty-printing objects if needed
                  if (value.length === 2) {
                    const oldPretty = prettyPrintIfObject(value[0], 1);
                    const newPretty = prettyPrintIfObject(value[1], 1);
                    const isOldMultiline = oldPretty.includes("\n");
                    const isNewMultiline = newPretty.includes("\n");
                    if (isOldMultiline || isNewMultiline) {
                      return `${
                        isOldMultiline
                          ? "\n" + indent(1) + oldPretty
                          : oldPretty
                      } ==> ${
                        isNewMultiline
                          ? "\n" + indent(1) + newPretty
                          : newPretty
                      }`;
                    } else {
                      return `${oldPretty} ==> ${newPretty}`;
                    }
                  }
                  // Handle arrays (not [old, new])
                  if (value.length === 0) return "[]";
                  return (
                    "[\n" +
                    value
                      .map(
                        (item) =>
                          indent(level + 2) + formatValue(item, level + 2)
                      )
                      .join("\n") +
                    "\n" +
                    indent(level + 1) +
                    "]"
                  );
                } else if (typeof value === "object" && value !== null) {
                  // Pretty-print objects with keys, otherwise show '{}'
                  if (Object.keys(value).length === 0) return "{}";
                  return (
                    "{\n" +
                    Object.entries(value)
                      .map(
                        ([k, v]) =>
                          indent(level + 1) +
                          `${k}: ${formatValue(v, level + 1)}`
                      )
                      .join("\n") +
                    "\n" +
                    indent(level) +
                    "}"
                  );
                } else {
                  // Fallback for primitives
                  return String(value);
                }
              }

              for (const key in source) {
                const currentItem = source[key];
                items.push(
                  <pre key={key}>
                    {key}: {formatValue(currentItem, 0)}
                  </pre>
                );
              }

              return <span className={styles.entries}>{items}</span>;
            },
          },
          {
            header: t("created_on"),
            accessorKey: "timestamp",
            id: "timestamp",
            filterVariant: "date",

            sortingFn: "datetime",
            Cell: ({ cell }: any) => {
              return <span>{replaceDate(cell.getValue(), true, true)}</span>;
            },
          },
          {
            header: t("modified_by"),
            accessorKey: "actor",
            customColumnFilterId: "actor__email",
            customSortingKey: "actor_email",
            id: "actor",
            Cell: ({ cell }: any) => {
              return <span>{cell.getValue().email}</span>;
            },
          },
        ]}
      />
      {errorModal}
    </RestrictedPage>
  );
}
